(() => {
  if (customElements.get('collection-product-detail')) return;

  const assetPromises = new Map();
  const scripts = new Set([
    'product-info.js', 'product-form.js', 'media-gallery.js', 'product-modal.js',
    'show-more.js', 'price-per-item.js', 'quantity-popover.js', 'recipient-form.js',
    'pickup-availability.js', 'product-model.js', 'quick-add.js', 'lepro-product.js',
  ]);

  function loadAsset(url, type) {
    const absolute = new URL(url, location.href).href;
    if (assetPromises.has(absolute)) return assetPromises.get(absolute);
    const existing = type === 'script'
      ? Array.from(document.scripts).some(node => node.src === absolute)
      : Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(node => node.href === absolute);
    if (existing) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const node = document.createElement(type === 'script' ? 'script' : 'link');
      if (type === 'script') { node.src = absolute; node.async = false; }
      else { node.rel = 'stylesheet'; node.href = absolute; }
      node.onload = resolve;
      node.onerror = () => { node.remove(); assetPromises.delete(absolute); reject(new Error(`Unable to load ${absolute}`)); };
      document.head.appendChild(node);
    });
    assetPromises.set(absolute, promise);
    return promise;
  }

  class CollectionProductDetail extends ModalDialog {
    constructor() {
      super();
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && document.querySelector('[data-collection-detail-owned][open]')) event.stopPropagation();
      });
      this.content = this.querySelector('[data-detail-content]');
      this.status = this.querySelector('[data-detail-status]');
      this.error = this.querySelector('[data-detail-error]');
      this.footer = this.querySelector('[data-detail-footer]');
      // overflow:hidden containers can still be scrolled by focus or browser reveal logic.
      // Only the inner content panel may scroll; keep the shell and footer in place.
      this.addEventListener('scroll', event => {
        if (event.target !== this && event.target !== this.querySelector('.collection-product-detail__dialog')) return;
        event.target.scrollTop = 0;
        event.target.scrollLeft = 0;
      }, true);
      this.addEventListener('input', () => this.scheduleFooterSync());
      this.addEventListener('change', () => this.scheduleFooterSync());
      this.addEventListener('submit', event => {
        if (event.submitter?.matches('[data-collection-detail-action]')) {
          this.activeAction = event.submitter;
          this.scheduleFooterSync();
        }
      }, true);
      this.footerObserver = new MutationObserver(() => this.scheduleFooterSync());
      this.footerObserver.observe(this.content, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'hidden', 'data-original', 'data-saving'],
      });
      this.addEventListener('product-info:loaded', event => this.installProductInfo(event.target));
      // Child image/info dialogs release the body lock when closing; restore the parent modal's lock.
      document.body.addEventListener('modalClosed', () => {
        setTimeout(() => {
          if (!this.hasAttribute('open')) return;
          document.body.classList.add('overflow-hidden');
          if (!document.querySelector('[data-collection-detail-owned][open]')) {
            const focus = this.contains(document.activeElement) ? document.activeElement : this.querySelector('[role="dialog"]');
            trapFocus(this, focus);
          }
        });
      });
    }

    async show(opener, productUrl = opener.dataset.collectionDetailUrl, variant = opener.dataset.collectionDetailVariant) {
      this.dataset.checkoutDirect = opener.dataset.checkoutDirect === 'true' ? 'true' : 'false';
      this.controller?.abort();
      this.clearLoadingOpener();
      this.content.querySelectorAll('product-info').forEach(info => info.abortController?.abort());
      this.status.hidden = true;
      this.error.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      const controller = this.controller;
      const url = new URL(productUrl, location.origin);
      if (url.origin !== location.origin) return;
      if (variant) url.searchParams.set('variant', variant);
      this.querySelector('[data-detail-fallback]').href = url.href;
      this.loadingOpener = opener;
      opener.setAttribute('aria-busy', 'true');
      opener.classList.add('loading');
      try {
        const response = await fetch(url.href, { signal: controller.signal });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('product-info[id^="MainProduct-"]');
        if (!source?.querySelector('.lepro-product-container')) throw new Error('Product detail content missing');
        const stylesheetLoads = Array.from(source.querySelectorAll('link[rel="stylesheet"]'), node => loadAsset(node.href, 'style'));
        for (const script of html.querySelectorAll('script[src]')) {
          const filename = new URL(script.src).pathname.split('/').pop();
          if (scripts.has(filename)) await loadAsset(script.src, 'script');
        }
        await Promise.all(stylesheetLoads);
        if (controller.signal.aborted) return;
        if (!customElements.get('product-info') || !customElements.get('product-form')) throw new Error('Product components unavailable');
        this.prepareHTML(html);
        const productInfo = html.querySelector('product-info');
        const node = document.importNode(productInfo, true);
        customElements.upgrade(node);
        // Instance methods are installed before connectedCallback initializes product-info.
        this.installProductInfo(node);
        this.removeOwnedModals();
        this.content.replaceChildren(node);
        this.applyQuantity(Number(opener.dataset.collectionDetailQuantity));
        this.status.hidden = true;
        this.classList.add('collection-product-detail--ready');
        this.syncFooter();
        this.lockPageScroll();
        this.startViewportTracking();
        super.show(opener);
        this.querySelector('[data-detail-scroll]').scrollTop = 0;
        this.content.querySelectorAll('slider-component, slideshow-component').forEach(slider => slider.resetPages?.());
        window.Shopify?.PaymentButton?.init();
        window.ProductModel?.loadShopifyXR?.();
      } catch (error) {
        if (controller.signal.aborted) return;
        this.status.hidden = true;
        this.removeOwnedModals();
        this.content.replaceChildren();
        this.error.hidden = false;
        this.footer.hidden = true;
        this.classList.remove('collection-product-detail--ready');
        this.lockPageScroll();
        this.startViewportTracking();
        super.show(opener);
        console.error('Collection product detail:', error);
      } finally {
        if (this.controller === controller) {
          this.clearLoadingOpener();
          this.content.removeAttribute('aria-busy');
          this.syncFooter();
        }
      }
    }

    applyQuantity(requested) {
      const input = this.content.querySelector('.quantity__input');
      if (!input || !Number.isFinite(requested) || requested <= 0) return;
      const min = Number(input.min || 1);
      const step = Number(input.step) > 0 ? Number(input.step) : 1;
      let value = min + Math.max(0, Math.ceil((requested - min) / step)) * step;
      if (input.max !== '') {
        const max = Number(input.max);
        value = Math.min(value, min + Math.floor((max - min) / step) * step);
      }
      if (value < min) return;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    lockPageScroll() {
      if (this.pageScrollLock) return;
      const body = document.body;
      const properties = ['position', 'top', 'left', 'width'];
      this.pageScrollLock = {
        x: window.scrollX, y: window.scrollY,
        styles: properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]),
      };
      document.documentElement.classList.add('collection-detail-scroll-locked');
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', -this.pageScrollLock.y + 'px');
      body.style.setProperty('left', -this.pageScrollLock.x + 'px');
      body.style.setProperty('width', '100%');
      this.trackTouch = event => {
        if (event.touches.length !== 1) return;
        this.touchPoint = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      };
      this.containTouch = event => {
        if (event.touches.length !== 1 || !this.touchPoint) return;
        const touch = event.touches[0];
        const dx = touch.clientX - this.touchPoint.x, dy = touch.clientY - this.touchPoint.y;
        this.touchPoint = { x: touch.clientX, y: touch.clientY };
        // Keep horizontal gallery gestures native. Vertical gestures must stay inside a scrollable panel.
        if (Math.abs(dx) > Math.abs(dy)) return;
        let node = event.target instanceof Element ? event.target : event.target.parentElement;
        while (node && node !== this) {
          const overflow = getComputedStyle(node).overflowY;
          if (/auto|scroll/.test(overflow) && node.scrollHeight > node.clientHeight + 1) {
            if ((dy < 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) ||
                (dy > 0 && node.scrollTop > 0)) return;
          }
          node = node.parentElement;
        }
        if (event.cancelable) event.preventDefault();
      };
      this.addEventListener('touchstart', this.trackTouch, { passive: true });
      this.addEventListener('touchmove', this.containTouch, { passive: false });
    }

    unlockPageScroll() {
      if (!this.pageScrollLock) return;
      const lock = this.pageScrollLock;
      this.pageScrollLock = null;
      this.removeEventListener('touchstart', this.trackTouch);
      this.removeEventListener('touchmove', this.containTouch);
      this.touchPoint = null;
      lock.styles.forEach(([name, value, priority]) => {
        if (value) document.body.style.setProperty(name, value, priority);
        else document.body.style.removeProperty(name);
      });
      document.documentElement.classList.remove('collection-detail-scroll-locked');
      const root = document.documentElement;
      const scrollBehavior = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(lock.x, lock.y);
      if (scrollBehavior) root.style.setProperty('scroll-behavior', scrollBehavior, priority);
      else root.style.removeProperty('scroll-behavior');
    }

    startViewportTracking() {
      this.stopViewportTracking();
      this.updateViewport = () => {
        const viewport = window.visualViewport;
        this.style.setProperty('--detail-viewport-height', (viewport?.height || window.innerHeight) + 'px');
        this.style.setProperty('--detail-viewport-top', (viewport?.offsetTop || 0) + 'px');
      };
      this.updateViewport();
      window.addEventListener('resize', this.updateViewport);
      window.visualViewport?.addEventListener('resize', this.updateViewport);
      window.visualViewport?.addEventListener('scroll', this.updateViewport);
    }

    stopViewportTracking() {
      if (!this.updateViewport) return;
      window.removeEventListener('resize', this.updateViewport);
      window.visualViewport?.removeEventListener('resize', this.updateViewport);
      window.visualViewport?.removeEventListener('scroll', this.updateViewport);
      this.updateViewport = null;
    }

    prepareHTML(html) {
      let info = html.querySelector('product-info');
      if (!info || info.dataset.collectionDetailPrepared) return;
      const originalSection = info.dataset.section;
      const variantFields = info.querySelector('lepro-fixed-add-cart #product-variant-fields');
      const namespace = `collection-detail-${originalSection}`;
      // Keep the native ID patterns (price-, ProductInfo-, ModalClose-) for theme components.
      const parsed = new DOMParser().parseFromString(info.outerHTML.replaceAll(originalSection, namespace), 'text/html');
      const prepared = parsed.querySelector('product-info');
      prepared.dataset.section = namespace;
      prepared.dataset.originalSection = namespace;
      prepared.dataset.collectionSourceSection = originalSection;
      prepared.dataset.collectionDetailPrepared = 'true';
      prepared.dataset.updateUrl = 'false';
      prepared.removeAttribute('data-zoom-on-hover');
      // Keep exactly the selected container; fixed page bars and SEO data stay on the product page.
      const container = prepared.querySelector('.lepro-product-container');
      Array.from(prepared.children).forEach(child => {
        if (child !== container && child.tagName !== 'STYLE') child.remove();
      });
      prepared.querySelectorAll('.custom-description, script:not([type="application/json"]), link, lepro-fixed-add-cart').forEach(node => node.remove());
      if (variantFields) {
        const fields = variantFields.cloneNode(true);
        fields.id = 'CollectionDetailVariant-' + namespace;
        fields.setAttribute('data-detail-variant-fields', '');
        prepared.appendChild(fields);
      }
      prepared.querySelectorAll('.scroll-trigger').forEach(node => node.classList.add('scroll-trigger--cancel'));
      prepared.querySelectorAll('.product__column-sticky').forEach(node => node.classList.remove('product__column-sticky'));
      prepared.querySelectorAll('modal-dialog, product-modal, quick-add-modal').forEach(node => {
        node.dataset.collectionDetailOwned = this.id;
      });
      // IDs without the section ID (for example complementary sliders) need their own suffix.
      const ids = new Map();
      prepared.querySelectorAll('[id]').forEach(node => {
        const oldId = node.getAttribute('id');
        if (!oldId.includes(namespace) && !oldId.startsWith('ProductJSON-')) {
          const newId = `${oldId}--collection-detail`;
          node.setAttribute('id', newId);
          ids.set(oldId, newId);
        }
      });
      prepared.querySelectorAll('*').forEach(node => {
        ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(attribute => {
          if (!node.hasAttribute(attribute)) return;
          node.setAttribute(attribute, node.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
        });
        ['href', 'data-modal', 'data-target'].forEach(attribute => {
          const value = node.getAttribute(attribute);
          if (value?.startsWith('#') && ids.has(value.slice(1))) node.setAttribute(attribute, `#${ids.get(value.slice(1))}`);
        });
      });
      info.replaceWith(prepared);
    }

    installProductInfo(info) {
      if (info.tagName !== 'PRODUCT-INFO' || info.collectionDetailInstalled) return;
      info.collectionDetailInstalled = true;
      info.dataset.updateUrl = 'false';
      info.prepareCollectionDetailHTML = html => this.prepareHTML(html);
      const nativeUpdate = info.handleUpdateProductInfo.bind(info);
      info.handleUpdateProductInfo = productUrl => {
        const update = nativeUpdate(productUrl);
        return html => {
          const fields = html.querySelector('[data-detail-variant-fields]');
          info.querySelector('[data-detail-variant-fields]')?.remove();
          if (fields) info.appendChild(fields.cloneNode(true));
          const scroller = this.querySelector('[data-detail-scroll]');
          const scrollTop = scroller.scrollTop;
          const quantity = Number(info.querySelector('.quantity__input')?.value);
          update(html);
          this.applyQuantity(quantity);
          scroller.scrollTop = scrollTop;
          this.syncFooter();
        };
      };
      const nativeBuildRequest = info.buildRequestUrlWithParams.bind(info);
      info.buildRequestUrlWithParams = (...args) => nativeBuildRequest(...args).replace(
        `section_id=${info.dataset.section}`, `section_id=${info.dataset.collectionSourceSection}`
      );
      info.querySelectorAll('product-recommendations').forEach(recommendations => {
        recommendations.loadRecommendations = async productId => {
          try {
            const url = new URL(recommendations.dataset.url, location.origin);
            url.searchParams.set('product_id', productId);
            url.searchParams.set('section_id', info.dataset.collectionSourceSection);
            const response = await fetch(url.href, { signal: this.controller.signal });
            if (!response.ok) throw new Error('Recommendations unavailable');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            this.prepareHTML(html);
            const source = html.querySelector('product-recommendations');
            if (!recommendations.isConnected) return;
            if (source?.innerHTML.trim()) recommendations.innerHTML = source.innerHTML;
            if (!recommendations.querySelector('slideshow-component')) recommendations.remove();
          } catch (error) {
            if (error.name !== 'AbortError') console.error('Collection recommendations:', error);
          }
        };
      });
      info.addPreProcessCallback(node => {
        if (node.matches('product-info')) this.removeOwnedModals();
      });
      // Custom attribute groups link to sibling products; keep normal clicks within the dialog.
      info.addEventListener('click', event => {
        const link = event.target.closest('.le-product-variant-picker a[href]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href, location.origin);
        if (url.origin !== location.origin) return;
        event.preventDefault();
        this.show(this.openedBy, url.href, null);
      });
    }

    scheduleFooterSync() {
      if (this.footerFrame) return;
      this.footerFrame = requestAnimationFrame(() => {
        this.footerFrame = null;
        this.syncFooter();
      });
    }

    syncFooter() {
      const info = this.content.querySelector('product-info');
      const productForm = info?.productForm;
      const form = productForm?.form;
      const submit = productForm?.submitButton;
      this.footer.hidden = !form;
      if (!form || !submit) return;
      const quantityInput = info.querySelector('.quantity__input');
      const quantity = Number(quantityInput?.value || 1);
      const busy = submit.getAttribute('aria-disabled') === 'true';
      const disabled = submit.disabled || busy || this.content.hasAttribute('aria-busy') || !productForm.variantIdInput.value
        || !Number.isFinite(quantity) || quantity <= 0 || (quantityInput && !quantityInput.checkValidity());
      this.footer.querySelectorAll('[data-collection-detail-action]').forEach(button => {
        button.setAttribute('form', form.getAttribute('id'));
        button.disabled = Boolean(disabled);
        button.classList.toggle('loading', busy && button === this.activeAction);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy || button !== this.activeAction);
      });
      if (!busy) this.activeAction = null;
      const error = productForm.querySelector('.product-form__error-message-wrapper');
      const footerError = this.footer.querySelector('[data-detail-footer-error]');
      footerError.textContent = error && !error.hidden ? error.textContent.trim() : '';
      footerError.hidden = !footerError.textContent;
      let fields = {};
      try { fields = JSON.parse(info.querySelector('[data-detail-variant-fields]')?.textContent || '{}'); } catch (_) { /* Fall back to the selected variant. */ }
      let variant = fields.variantPorduct;
      if (!variant) {
        try { variant = JSON.parse(info.querySelector('[data-selected-variant]')?.textContent || '{}'); } catch (_) { variant = {}; }
      }
      const priceNode = info.querySelector('[id^="price-"] .price-sale');
      const base = Number(variant.price ?? priceNode?.dataset.original);
      const total = this.footer.querySelector('[data-detail-total]');
      if (!Number.isFinite(base)) { total.textContent = priceNode?.textContent || ''; return; }
      const isB2B = fields.isB2BCustomer ?? window.isB2BCustomer;
      const tier = typeof getVolumeTierPricePence === 'function' ? getVolumeTierPricePence(quantity, info) : null;
      const toggle = info.querySelector('lepro-off-code .discount-toggle');
      let unit = base;
      if (tier !== null && tier < base) unit = tier;
      else if (!isB2B && (!toggle || toggle.checked)) {
        const discount = fields.metafieldsCustoms?.variants_discounts_and_marketing?.product_detail_max_discount?.discount;
        if (discount) unit = Math.round(Number(window.ShopifyConstant.setDiscountPrice(base, discount)) * 100);
        else if (toggle?.checked) unit = Math.max(0, base - Number(priceNode?.dataset.saving || 0));
      }
      const reference = isB2B && variant.compare_at_price > base ? variant.compare_at_price : base;
      const currency = fields.currencySymbol || '';
      const money = cents => currency + window.ShopifyConstant.formatMoney(cents / 100);
      const amount = document.createElement('span');
      amount.className = 'price-sale';
      amount.textContent = money(Math.round(unit * quantity));
      total.replaceChildren(amount);
      if (reference > unit) {
        const compare = document.createElement('s');
        compare.className = 'price-regular';
        compare.textContent = money(Math.round(reference * quantity));
        total.appendChild(compare);
      }
    }

    clearLoadingOpener() {
      this.loadingOpener?.removeAttribute('aria-busy');
      this.loadingOpener?.classList.remove('loading');
      this.loadingOpener = null;
    }

    removeOwnedModals() {
      document.querySelectorAll('[data-collection-detail-owned]').forEach(node => {
        if (node.dataset.collectionDetailOwned === this.id) node.remove();
      });
    }

    hide(preventFocus = false) {
      this.stopViewportTracking();
      this.controller?.abort();
      this.clearLoadingOpener();
      this.footer.hidden = true;
      this.content.querySelectorAll('product-info').forEach(info => info.abortController?.abort());
      const cart = document.querySelector('cart-notification, cart-drawer');
      if (cart && this.openedBy) cart.setActiveElement(this.openedBy);
      const opener = this.openedBy;
      if (preventFocus) this.openedBy = null;
      super.hide();
      this.unlockPageScroll();
      this.removeOwnedModals();
      // product-form finishes its promise chain after hide; allow that cleanup to finish first.
      setTimeout(() => { if (!this.hasAttribute('open')) this.content.replaceChildren(); });
      if (opener) opener.removeAttribute('aria-busy');
    }
  }

  customElements.define('collection-product-detail', CollectionProductDetail);
  document.addEventListener('click', event => {
    const button = event.target.closest('button[data-collection-detail-url]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const section = button.closest('.shopify-section');
    const modal = section && document.getElementById(`CollectionProductDetail-${section.id.replace('shopify-section-', '')}`);
    if (!modal) return;
    event.preventDefault();
    modal.show(button);
  });
})();
